import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Button,
  Badge,
  Input,
  Label,
  Textarea,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
  Spinner,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '@rnd-payplus/alloy-react';
import { KPICard } from '@rnd-payplus/alloy-react/patterns';
import {
  Plus,
  Trash2,
  Play,
  Pause,
  Square,
  RotateCcw,
  Eye,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { usePageShell } from '../contexts/PageShellContext';
import {
  ReactFlow,
  Background,
  Controls,
  Handle,
  Position,
  type Node,
  type Edge,
  type NodeProps,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

interface WorkflowDefinition {
  id: string;
  name: string;
  description: string | null;
  steps: any;
  config: any;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface WorkflowInstance {
  id: string;
  definitionId: string;
  status: string;
  currentStep: number;
  context: any;
  errorMessage: string | null;
  retryCount: number;
  startedAt: string | null;
  completedAt: string | null;
  correlationId: string;
  createdAt: string;
}

interface StepLog {
  id: string;
  stepIndex: number;
  stepType: string;
  status: string;
  errorMessage: string | null;
  durationMs: number | null;
  startedAt: string;
  completedAt: string | null;
}

interface WorkflowMetrics {
  totalInstances: number;
  pending: number;
  running: number;
  completed: number;
  failed: number;
  cancelled: number;
  avgDurationMs: number;
}

const statusVariants: Record<string, 'default' | 'destructive' | 'secondary' | 'outline'> = {
  completed: 'default',
  failed: 'destructive',
  pending: 'outline',
  running: 'secondary',
  paused: 'secondary',
  cancelled: 'secondary',
};

// Routed through dashboard-backend so the day a /api/workflows proxy lands
// these calls work without further frontend changes. Until then they 404 and
// the user sees a real error instead of fabricated mock rows.
const ORCHESTRATOR_URL = '';

// --- React Flow custom nodes for workflow visualization ---

const stepTypeColors: Record<string, string> = {
  extract: 'bg-blue-50 text-blue-700 border-blue-200',
  transform: 'bg-purple-50 text-purple-700 border-purple-200',
  validate: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  load: 'bg-green-50 text-green-700 border-green-200',
  notify: 'bg-orange-50 text-orange-700 border-orange-200',
};

function WorkflowStartNode({ data }: NodeProps) {
  return (
    <div className="rounded-full border-2 border-green-500 bg-green-50 px-5 py-2 shadow-sm">
      <span className="text-sm font-semibold text-green-700">{data.label as string}</span>
      <Handle type="source" position={Position.Right} className="!bg-green-500 !w-2.5 !h-2.5" />
    </div>
  );
}

function WorkflowStepNode({ data }: NodeProps) {
  const colorClass = stepTypeColors[data.stepType as string] || 'bg-gray-50 text-gray-700 border-gray-200';
  return (
    <div className="rounded-lg border bg-card px-4 py-3 shadow-sm min-w-[140px]">
      <Handle type="target" position={Position.Left} className="!bg-primary !w-2.5 !h-2.5" />
      <div className="flex items-center gap-2 mb-1">
        <Badge variant="outline" className={`text-xs ${colorClass}`}>
          {(data.stepType as string) || 'step'}
        </Badge>
      </div>
      <p className="text-sm font-medium">{data.label as string}</p>
      <Handle type="source" position={Position.Right} className="!bg-primary !w-2.5 !h-2.5" />
    </div>
  );
}

function WorkflowEndNode({ data }: NodeProps) {
  return (
    <div className="rounded-full border-2 border-red-400 bg-red-50 px-5 py-2 shadow-sm">
      <Handle type="target" position={Position.Left} className="!bg-red-400 !w-2.5 !h-2.5" />
      <span className="text-sm font-semibold text-red-600">{data.label as string}</span>
    </div>
  );
}

const workflowNodeTypes = {
  workflowStart: WorkflowStartNode,
  workflowStep: WorkflowStepNode,
  workflowEnd: WorkflowEndNode,
};

const WorkflowsPage = () => {
  const [activeTab, setActiveTab] = useState('definitions');
  const [definitions, setDefinitions] = useState<WorkflowDefinition[]>([]);
  const [instances, setInstances] = useState<WorkflowInstance[]>([]);
  const [metrics, setMetrics] = useState<WorkflowMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  // Create definition dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [newDef, setNewDef] = useState({ name: '', description: '', steps: '[]', config: '{}' });

  // Instance detail dialog
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedInstance, setSelectedInstance] = useState<WorkflowInstance | null>(null);
  const [stepLogs, setStepLogs] = useState<StepLog[]>([]);

  const [selectedDefId, setSelectedDefId] = useState<string | null>(null);

  const selectedDef = useMemo(
    () => definitions.find((d) => d.id === selectedDefId) || definitions[0] || null,
    [definitions, selectedDefId]
  );

  const { workflowNodes, workflowEdges } = useMemo(() => {
    if (!selectedDef) return { workflowNodes: [] as Node[], workflowEdges: [] as Edge[] };

    const steps = Array.isArray(selectedDef.steps) ? selectedDef.steps : [];
    const nodes: Node[] = [];
    const edges: Edge[] = [];
    const colWidth = 200;

    // Start node
    nodes.push({
      id: 'wf-start',
      type: 'workflowStart',
      position: { x: 0, y: 0 },
      data: { label: 'Start' },
    });

    // Step nodes
    steps.forEach((step: any, i: number) => {
      const stepId = `wf-step-${i}`;
      nodes.push({
        id: stepId,
        type: 'workflowStep',
        position: { x: (i + 1) * colWidth, y: 0 },
        data: {
          label: step.name || step.type || `Step ${i + 1}`,
          stepType: step.type || 'step',
        },
      });

      edges.push({
        id: `wf-edge-${i}`,
        source: i === 0 ? 'wf-start' : `wf-step-${i - 1}`,
        target: stepId,
        animated: true,
        style: { stroke: '#6366f1', strokeWidth: 2 },
      });
    });

    // End node
    const endId = 'wf-end';
    nodes.push({
      id: endId,
      type: 'workflowEnd',
      position: { x: (steps.length + 1) * colWidth, y: 0 },
      data: { label: 'End' },
    });

    edges.push({
      id: 'wf-edge-end',
      source: steps.length > 0 ? `wf-step-${steps.length - 1}` : 'wf-start',
      target: endId,
      animated: true,
      style: { stroke: '#6366f1', strokeWidth: 2 },
    });

    return { workflowNodes: nodes, workflowEdges: edges };
  }, [selectedDef]);

  const tenantId = localStorage.getItem('currentTenantId') || 'default';

  const fetchDefinitions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${ORCHESTRATOR_URL}/api/workflows/definitions`, {
        headers: { 'X-Tenant-ID': tenantId },
      });
      if (!response.ok) throw new Error(`Failed to load workflow definitions (${response.status})`);
      setDefinitions(await response.json());
    } catch (error) {
      console.error('Error fetching definitions:', error);
      setDefinitions([]);
      toast.error(error instanceof Error ? error.message : 'Failed to load workflow definitions');
    }
    setLoading(false);
  }, [tenantId]);

  const fetchInstances = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page + 1),
        pageSize: String(rowsPerPage),
      });
      if (statusFilter !== 'all') params.append('status', statusFilter);

      const response = await fetch(`${ORCHESTRATOR_URL}/api/workflows/instances?${params}`, {
        headers: { 'X-Tenant-ID': tenantId },
      });
      if (!response.ok) throw new Error(`Failed to load workflow instances (${response.status})`);
      const data = await response.json();
      setInstances(data.items || []);
      setTotalCount(data.totalCount || 0);
    } catch (error) {
      console.error('Error fetching instances:', error);
      setInstances([]);
      setTotalCount(0);
      toast.error(error instanceof Error ? error.message : 'Failed to load workflow instances');
    }
    setLoading(false);
  }, [tenantId, page, rowsPerPage, statusFilter]);

  const fetchMetrics = useCallback(async () => {
    try {
      const response = await fetch(`${ORCHESTRATOR_URL}/api/workflows/metrics`, {
        headers: { 'X-Tenant-ID': tenantId },
      });
      if (!response.ok) throw new Error(`Failed to load workflow metrics (${response.status})`);
      setMetrics(await response.json());
    } catch (error) {
      console.error('Error fetching metrics:', error);
      setMetrics(null);
    }
  }, [tenantId]);

  useEffect(() => {
    if (activeTab === 'definitions') fetchDefinitions();
    else fetchInstances();
    fetchMetrics();
  }, [activeTab, fetchDefinitions, fetchInstances, fetchMetrics]);

  const handleCreateDefinition = async () => {
    try {
      let stepsJson, configJson;
      try { stepsJson = JSON.parse(newDef.steps); } catch { toast.error('Invalid steps JSON'); return; }
      try { configJson = JSON.parse(newDef.config); } catch { toast.error('Invalid config JSON'); return; }

      const response = await fetch(`${ORCHESTRATOR_URL}/api/workflows/definitions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Tenant-ID': tenantId },
        body: JSON.stringify({ name: newDef.name, description: newDef.description, steps: stepsJson, config: configJson }),
      });

      if (response.ok) {
        toast.success('Definition created');
        setCreateOpen(false);
        setNewDef({ name: '', description: '', steps: '[]', config: '{}' });
        fetchDefinitions();
      } else {
        toast.error('Failed to create definition');
      }
    } catch {
      toast.error('Failed to create definition');
    }
  };

  const handleDeleteDefinition = async (id: string) => {
    try {
      await fetch(`${ORCHESTRATOR_URL}/api/workflows/definitions/${id}`, {
        method: 'DELETE',
        headers: { 'X-Tenant-ID': tenantId },
      });
      toast.success('Definition deleted');
      fetchDefinitions();
    } catch {
      toast.error('Failed to delete definition');
    }
  };

  const handleStartWorkflow = async (definitionId: string) => {
    try {
      const response = await fetch(`${ORCHESTRATOR_URL}/api/workflows/instances`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Tenant-ID': tenantId },
        body: JSON.stringify({ definitionId }),
      });
      if (response.ok) {
        toast.success('Workflow started');
        setActiveTab('instances');
        fetchInstances();
      }
    } catch {
      toast.error('Failed to start workflow');
    }
  };

  const handleInstanceAction = async (id: string, action: 'cancel' | 'retry' | 'pause' | 'resume') => {
    try {
      const response = await fetch(`${ORCHESTRATOR_URL}/api/workflows/instances/${id}/${action}`, {
        method: 'POST',
        headers: { 'X-Tenant-ID': tenantId },
      });
      if (response.ok) {
        toast.success(`Workflow ${action} successful`);
        fetchInstances();
      } else {
        toast.error(`Cannot ${action} workflow in current state`);
      }
    } catch {
      toast.error(`Failed to ${action} workflow`);
    }
  };

  const handleViewLogs = async (instance: WorkflowInstance) => {
    setSelectedInstance(instance);
    try {
      const response = await fetch(`${ORCHESTRATOR_URL}/api/workflows/instances/${instance.id}/logs`, {
        headers: { 'X-Tenant-ID': tenantId },
      });
      if (!response.ok) throw new Error(`Failed to load step logs (${response.status})`);
      setStepLogs(await response.json());
    } catch (error) {
      console.error('Error fetching step logs:', error);
      setStepLogs([]);
      toast.error(error instanceof Error ? error.message : 'Failed to load step logs');
    }
    setDetailOpen(true);
  };

  const formatDate = (date: string | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleString();
  };

  const formatDuration = (ms: number | null) => {
    if (ms == null) return '-';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const getDefName = (defId: string) => {
    const def = definitions.find(d => d.id === defId);
    return def?.name || defId.slice(0, 8);
  };

  const totalPages = Math.ceil(totalCount / rowsPerPage);

  usePageShell({
    title: 'Workflows',
    primaryAction: activeTab === 'definitions' ? (
      <Button onClick={() => setCreateOpen(true)}>
        <Plus className="mr-2 h-4 w-4" />
        New Definition
      </Button>
    ) : undefined,
    secondaryActions: (
      <Button variant="outline" onClick={() => activeTab === 'definitions' ? fetchDefinitions() : fetchInstances()}>
        <RefreshCw className="mr-2 h-4 w-4" />
        Refresh
      </Button>
    ),
    breadcrumbs: [
      { label: 'Home', href: '/dashboard' },
      { label: 'Workflows' },
    ],
  });

  return (
    <TooltipProvider>
      <div>
        {/* Metrics Cards */}
        {metrics && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4 mb-6">
            {[
              { label: 'Total', value: metrics.totalInstances },
              { label: 'Completed', value: metrics.completed },
              { label: 'Running', value: metrics.running },
              { label: 'Pending', value: metrics.pending },
              { label: 'Failed', value: metrics.failed },
              { label: 'Avg Duration', value: formatDuration(metrics.avgDurationMs) },
            ].map((metric) => (
              <KPICard key={metric.label} title={metric.label} value={metric.value} />
            ))}
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setPage(0); }}>
          <TabsList className="w-full mb-4">
            <TabsTrigger className="flex-1" value="definitions">Definitions</TabsTrigger>
            <TabsTrigger className="flex-1" value="instances">Instances</TabsTrigger>
          </TabsList>

          {/* Definitions Tab */}
          <TabsContent value="definitions">
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Steps</TableHead>
                        <TableHead>Schedule</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-8">
                            <Spinner className="mx-auto" />
                          </TableCell>
                        </TableRow>
                      ) : definitions.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                            No workflow definitions found
                          </TableCell>
                        </TableRow>
                      ) : definitions.map((def) => (
                        <TableRow key={def.id}>
                          <TableCell className="font-medium">{def.name}</TableCell>
                          <TableCell className="max-w-[250px] truncate">
                            {def.description || '-'}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {(Array.isArray(def.steps) ? def.steps : []).map((s: any, i: number) => (
                                <Badge key={i} variant="secondary">{s.type}</Badge>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell>
                            {def.config?.schedule ? (
                              <Badge variant="outline">{def.config.schedule}</Badge>
                            ) : '-'}
                          </TableCell>
                          <TableCell>{formatDate(def.createdAt)}</TableCell>
                          <TableCell className="text-right whitespace-nowrap">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button variant="ghost" size="icon" onClick={() => handleStartWorkflow(def.id)}>
                                  <Play className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Start workflow</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive" onClick={() => handleDeleteDefinition(def.id)}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Delete</TooltipContent>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
            {/* Workflow Flow Visualization */}
            {definitions.length > 0 && (
              <div className="mt-4">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-sm font-semibold text-muted-foreground">Workflow Steps Visualization</h3>
                  {definitions.length > 1 && (
                    <Select
                      value={selectedDefId || definitions[0]?.id || ''}
                      onValueChange={(val) => setSelectedDefId(val)}
                    >
                      <SelectTrigger className="w-[240px] h-8">
                        <SelectValue placeholder="Select definition" />
                      </SelectTrigger>
                      <SelectContent>
                        {definitions.map((d) => (
                          <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div className="h-[400px] rounded-md border">
                  <ReactFlow
                    nodes={workflowNodes}
                    edges={workflowEdges}
                    nodeTypes={workflowNodeTypes}
                    fitView
                    proOptions={{ hideAttribution: true }}
                  >
                    <Background />
                    <Controls />
                  </ReactFlow>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Instances Tab */}
          <TabsContent value="instances">
                <div className="mb-4">
                  <Select
                    value={statusFilter}
                    onValueChange={(value) => { setStatusFilter(value); setPage(0); }}
                  >
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="running">Running</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="failed">Failed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                      <SelectItem value="paused">Paused</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Workflow</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Step</TableHead>
                        <TableHead>Retries</TableHead>
                        <TableHead>Started</TableHead>
                        <TableHead>Completed</TableHead>
                        <TableHead>Error</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center py-8">
                            <Spinner className="mx-auto" />
                          </TableCell>
                        </TableRow>
                      ) : instances.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                            No workflow instances found
                          </TableCell>
                        </TableRow>
                      ) : instances.map((inst) => (
                        <TableRow key={inst.id}>
                          <TableCell className="font-medium">{getDefName(inst.definitionId)}</TableCell>
                          <TableCell>
                            <Badge variant={statusVariants[inst.status] || 'secondary'}>{inst.status}</Badge>
                          </TableCell>
                          <TableCell>{inst.currentStep}</TableCell>
                          <TableCell>{inst.retryCount}</TableCell>
                          <TableCell>{formatDate(inst.startedAt)}</TableCell>
                          <TableCell>{formatDate(inst.completedAt)}</TableCell>
                          <TableCell className="max-w-[200px] truncate">
                            {inst.errorMessage || '-'}
                          </TableCell>
                          <TableCell className="text-right whitespace-nowrap">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button variant="ghost" size="icon" onClick={() => handleViewLogs(inst)}>
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>View logs</TooltipContent>
                            </Tooltip>
                            {(inst.status === 'pending' || inst.status === 'running') && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive" onClick={() => handleInstanceAction(inst.id, 'cancel')}>
                                    <Square className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Cancel</TooltipContent>
                              </Tooltip>
                            )}
                            {inst.status === 'running' && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button variant="ghost" size="icon" onClick={() => handleInstanceAction(inst.id, 'pause')}>
                                    <Pause className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Pause</TooltipContent>
                              </Tooltip>
                            )}
                            {inst.status === 'paused' && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button variant="ghost" size="icon" onClick={() => handleInstanceAction(inst.id, 'resume')}>
                                    <Play className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Resume</TooltipContent>
                              </Tooltip>
                            )}
                            {inst.status === 'failed' && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button variant="ghost" size="icon" className="text-orange-500 hover:text-orange-600" onClick={() => handleInstanceAction(inst.id, 'retry')}>
                                    <RotateCcw className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Retry</TooltipContent>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between mt-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Rows per page:</span>
                    <Select
                      value={String(rowsPerPage)}
                      onValueChange={(value) => {
                        setRowsPerPage(parseInt(value, 10));
                        setPage(0);
                      }}
                    >
                      <SelectTrigger className="w-[70px] h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="25">25</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                      </SelectContent>
                    </Select>
                    <span className="text-sm text-muted-foreground">
                      {totalCount === 0
                        ? '0 of 0'
                        : `${page * rowsPerPage + 1}-${Math.min((page + 1) * rowsPerPage, totalCount)} of ${totalCount}`}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === 0}
                      onClick={() => setPage(page - 1)}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page >= totalPages - 1}
                      onClick={() => setPage(page + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
          </TabsContent>
        </Tabs>

        {/* Create Definition Dialog */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Create Workflow Definition</DialogTitle>
              <DialogDescription>
                Define the steps and configuration for a new workflow.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="def-name">Name</Label>
                <Input
                  id="def-name"
                  value={newDef.name}
                  onChange={(e) => setNewDef({ ...newDef, name: e.target.value })}
                  placeholder="Workflow name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="def-description">Description</Label>
                <Textarea
                  id="def-description"
                  rows={2}
                  value={newDef.description}
                  onChange={(e) => setNewDef({ ...newDef, description: e.target.value })}
                  placeholder="Optional description"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="def-steps">Steps (JSON array)</Label>
                <Textarea
                  id="def-steps"
                  rows={4}
                  value={newDef.steps}
                  onChange={(e) => setNewDef({ ...newDef, steps: e.target.value })}
                  placeholder='[{"type":"extract","config":{}},{"type":"transform","config":{}},{"type":"load","config":{}}]'
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="def-config">Config (JSON)</Label>
                <Textarea
                  id="def-config"
                  rows={2}
                  value={newDef.config}
                  onChange={(e) => setNewDef({ ...newDef, config: e.target.value })}
                  placeholder='{"schedule":"0 2 * * *"}'
                />
              </div>
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <Button onClick={handleCreateDefinition} disabled={!newDef.name}>Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Instance Detail / Step Logs Dialog */}
        <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
          <DialogContent className="sm:max-w-3xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                Workflow Instance: {selectedInstance?.id.slice(0, 8)}...
                {selectedInstance && (
                  <Badge variant={statusVariants[selectedInstance.status] || 'secondary'}>
                    {selectedInstance.status}
                  </Badge>
                )}
              </DialogTitle>
              <DialogDescription>
                View step execution details and logs.
              </DialogDescription>
            </DialogHeader>
            {selectedInstance && (
              <div className="mb-4">
                <p className="text-sm text-muted-foreground">
                  Correlation: {selectedInstance.correlationId} | Retries: {selectedInstance.retryCount}
                </p>
                {selectedInstance.errorMessage && (
                  <div className="mt-2 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
                    {selectedInstance.errorMessage}
                  </div>
                )}
              </div>
            )}
            <div>
              <h4 className="text-sm font-semibold mb-2">Step Execution Log</h4>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Step</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Started</TableHead>
                      <TableHead>Error</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stepLogs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell>{log.stepIndex}</TableCell>
                        <TableCell><Badge variant="secondary">{log.stepType}</Badge></TableCell>
                        <TableCell>
                          <Badge variant={statusVariants[log.status] || 'secondary'}>{log.status}</Badge>
                        </TableCell>
                        <TableCell>{formatDuration(log.durationMs)}</TableCell>
                        <TableCell>{formatDate(log.startedAt)}</TableCell>
                        <TableCell className="max-w-[200px] truncate">
                          {log.errorMessage || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                    {stepLogs.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground py-4">
                          No step logs yet
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">Close</Button>
              </DialogClose>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
};

export default WorkflowsPage;
